import { BaseEvent, DynamicEvent } from 'nexgen-types/events';
import { GlobalSocketService } from '@host/services/socket.service';
import { EventRegistry } from 'nexgen-types';
import { Logger } from '../../shared/services/logger.service';

type EventHandler = (event: BaseEvent) => void;

interface RegisteredHandler {
  event: string;
  handler: EventHandler;
}

interface DebounceConfig {
  enabled: boolean;
  defaultDelay: number;
  eventSpecificDelays: Map<string, number>;
}

interface PendingEvent {
  eventInstance: BaseEvent;
  broadcast: boolean;
  timeout: ReturnType<typeof setTimeout>;
}

export class EventService {
  private static instance: EventService;
  private listeners: Map<string, RegisteredHandler[]> = new Map();
  private wildcardListeners: Map<string, RegisteredHandler[]> = new Map();
  private registeredHandles: Map<string, RegisteredHandler> = new Map();
  private nextHandleId = 0;
  private namespace: string;

  private isReady = false;
  private eventQueue: Array<{ event_type: string; payload: any; broadcast: boolean }> = [];

  // Debouncing system
  private debounceConfig: DebounceConfig = {
    enabled: true,
    defaultDelay: 100, // 100ms default
    eventSpecificDelays: new Map([
      ['vo.user.room.changed', 150], // Room changes might need slightly longer
      ['vo.user.status.changed', 25], // Status updates need to be very fast for lock/unlock
      ['vo.user.status.changed.topic', 25], // Topic events also need to be fast
      ['vo.user.presence.*', 75], // Presence events moderate delay
    ]),
  };
  private pendingEvents: Map<string, PendingEvent> = new Map();

  private constructor(namespace: string = '/events') {
    this.namespace = namespace;

    GlobalSocketService.setEventHandler(this.namespace, (rawEvent: BaseEvent) => {
      Logger.debug('[EventService] Processing inbound socket event:', rawEvent.__type);
      Logger.debug('🔍 [DEBUG] EventService received rawEvent:', rawEvent);
      Logger.debug('🔍 [DEBUG] EventService rawEvent keys:', Object.keys(rawEvent));
      this.routeEvent(rawEvent.__type, rawEvent);
    });

    queueMicrotask(() => {
      this.isReady = true;
      this.flushQueue();
    });
  }

  static getInstance(namespace?: string): EventService {
    if (!EventService.instance) {
      EventService.instance = new EventService(namespace);
    }
    return EventService.instance;
  }

  // Debounce configuration methods
  configureDebounce(config: Partial<DebounceConfig>): void {
    this.debounceConfig = { ...this.debounceConfig, ...config };
  }

  setEventDelay(eventType: string, delayMs: number): void {
    this.debounceConfig.eventSpecificDelays.set(eventType, delayMs);
  }

  disableDebounce(): void {
    this.debounceConfig.enabled = false;
    // Clear any pending events
    for (const [, pending] of this.pendingEvents) {
      clearTimeout(pending.timeout);
      this.executeEmit(pending.eventInstance, pending.broadcast);
    }
    this.pendingEvents.clear();
  }

  /**
   * Safe emit that tries multiple methods
   */
  emitSafe<T extends object>(eventType: string, payload: T, broadcast = true): void {
    try {
      // Try 1: Registry-based emit
      this.emitViaRegistry(eventType, payload, broadcast);
    } catch (error) {
      Logger.warn('[EventService] Registry emit failed, trying dynamic emit:', error);

      try {
        // Try 2: Dynamic emit
        this.emit(eventType, payload, broadcast);
      } catch (fallbackError) {
        Logger.error('[EventService] All emit methods failed:', fallbackError);
        throw fallbackError;
      }
    }
  }

  /**
   * Emit using EventRegistry (if available)
   */
  emitViaRegistry<T extends object>(eventType: string, payload: T, broadcast = true): void {
    try {
      const eventDef = EventRegistry[eventType as keyof typeof EventRegistry];
      if (eventDef) {
        const eventInstance = eventDef.create(payload);
        this.emitTyped(eventInstance, broadcast);
        Logger.debug('[EventService] ✅ Emitted via EventRegistry');
      } else {
        Logger.warn(`[EventService] No registry entry for ${eventType}, using dynamic emit`);
        this.emit(eventType, payload, broadcast);
      }
    } catch (error) {
      Logger.error('[EventService] Registry emit failed, falling back to dynamic emit:', error);
      this.emit(eventType, payload, broadcast);
    }
  }

  emit<T extends object>(event_type: string, payload: T, broadcast = true): void {
    const eventInstance = new DynamicEvent(event_type, payload);
    this.emitTyped(eventInstance, broadcast);
  }

  emitTyped<T extends BaseEvent>(eventInstance: T, broadcast = true): void {
    const event_type = eventInstance.__type;
    if (!event_type) {
      Logger.warn('[EventService.emitTyped] Missing __type on event instance:', eventInstance);
      return;
    }

    if (!this.isReady) {
      Logger.debug(`[EventService] Queuing event until ready: ${event_type}`);
      this.eventQueue.push({ event_type, payload: eventInstance, broadcast });
      return;
    }

    this.executeEmit(eventInstance, broadcast);
  }

  // Debounced emit logic
  private debounceEmit<T extends BaseEvent>(eventInstance: T, broadcast: boolean): void {
    const eventType = eventInstance.__type;
    const debounceKey = this.getDebounceKey(eventInstance);

    // Get delay for this event type
    const delay = this.getDelayForEvent(eventType);

    // Cancel existing pending event if any
    const existing = this.pendingEvents.get(debounceKey);
    if (existing) {
      clearTimeout(existing.timeout);
      Logger.debug(`[EventService] 🚫 Debounced duplicate: ${eventType} (key: ${debounceKey})`);
    }

    // Schedule new event
    const timeout = setTimeout(() => {
      this.pendingEvents.delete(debounceKey);
      this.executeEmit(eventInstance, broadcast);
    }, delay);

    this.pendingEvents.set(debounceKey, {
      eventInstance,
      broadcast,
      timeout,
    });

    Logger.debug(
      `[EventService] ⏱️  Debounced '${eventType}' for ${delay}ms (key: ${debounceKey})`
    );
  }

  // Generate debounce key based on event type and key properties
  private getDebounceKey(eventInstance: BaseEvent): string {
    const eventType = eventInstance.__type;

    // For different event types, use different key strategies
    if (eventType.includes('room.changed')) {
      // Room changes: debounce per user
      return `${eventType}:${(eventInstance as any).userId || 'unknown'}`;
    }

    if (eventType.includes('status.changed')) {
      // Status changes: debounce per user
      return `${eventType}:${(eventInstance as any).user_pid || (eventInstance as any).userId || 'unknown'}`;
    }

    if (eventType.includes('presence')) {
      // Presence: debounce per user+room
      const userId = (eventInstance as any).user_pid || (eventInstance as any).userId || 'unknown';
      const roomId = (eventInstance as any).room_pid || (eventInstance as any).roomId || 'unknown';
      return `${eventType}:${userId}:${roomId}`;
    }

    // Default: debounce by event type only
    return eventType;
  }

  // Get delay for specific event type
  private getDelayForEvent(eventType: string): number {
    // Check exact match first
    if (this.debounceConfig.eventSpecificDelays.has(eventType)) {
      return this.debounceConfig.eventSpecificDelays.get(eventType)!;
    }

    // Check wildcard patterns
    for (const [pattern, delay] of this.debounceConfig.eventSpecificDelays.entries()) {
      if (pattern.includes('*')) {
        const regex = new RegExp('^' + pattern.replace(/\./g, '\\.').replace(/\*/g, '.*') + '$');
        if (regex.test(eventType)) {
          return delay;
        }
      }
    }

    return this.debounceConfig.defaultDelay;
  }

  // Execute the actual emit (separated for reuse)
  private executeEmit<T extends BaseEvent>(eventInstance: T, broadcast: boolean): void {
    const event_type = eventInstance.__type;

    // Forward raw event object to registered handlers
    this.routeEvent(event_type, eventInstance);

    if (broadcast) {
      // Send raw event object to socket service
      GlobalSocketService.emitEvent(this.namespace, eventInstance).catch((err) => {
        Logger.error('[EventService] Error broadcasting event:', err);
      });
    }
  }

  // === Overloaded on() signatures ===
  on(event: string, callback: EventHandler): void;
  on(params: { event: string; callback: EventHandler; handle?: string }): void;

  on(...args: any[]): string | undefined {
    let event: string;
    let handler: EventHandler;
    let explicitHandle: string | undefined;

    if (typeof args[0] === 'string' && typeof args[1] === 'function') {
      event = args[0];
      handler = args[1];
    } else if (typeof args[0] === 'object') {
      const params = args[0];
      event = params.event;
      handler = params.callback;
      explicitHandle = params.handle;
    } else {
      Logger.warn('[EventService] Invalid on() call', args);
      return undefined;
    }

    // Check if this exact handler function reference is already registered for this event.
    // This only catches true duplicates (the same function object registered twice) — it
    // cannot detect "logically the same handler" across separate `.bind(this)` calls, since
    // those produce distinct function objects. Callers that need on()/off() to pair up
    // correctly must cache and reuse the same bound function for both calls.
    const isWildcard = event.includes('*');
    const map = isWildcard ? this.wildcardListeners : this.listeners;
    const existingHandlers = map.get(event) || [];
    const isDuplicate = existingHandlers.some((registered) => registered.handler === handler);

    // Each registration gets its own unique handle, so distinct registrations for the same
    // event never collide or overwrite each other's tracking entry (previously the handle was
    // derived from the handler's stringified source, which is identical for every bound
    // function regardless of which .bind() call produced it — causing later registrations to
    // silently overwrite the tracking for earlier ones).
    const handle = explicitHandle ?? `H${++this.nextHandleId}`;

    if (isDuplicate) {
      Logger.debug(`[EventService] Duplicate handler skipped for '${event}'`);
      return undefined;
    }

    if (!map.has(event)) {
      map.set(event, []);
    }

    const registeredHandler: RegisteredHandler = { event, handler };
    map.get(event)!.push(registeredHandler);
    this.registeredHandles.set(handle, registeredHandler);

    Logger.debug(`[EventService] Registered '${event}' (handle: ${handle})`);
    return handle; // Return success indicator
  }

  off(event: string, handler: EventHandler): void {
    // Identity-based removal — only removes a listener registered with this exact function
    // reference. A freshly created `.bind(this)` result will never match one from an earlier
    // `on()` call; the fix at the call site is to cache and reuse the same bound function for
    // both on() and off(), not to work around it here.
    const isWildcard = event.includes('*');
    const map = isWildcard ? this.wildcardListeners : this.listeners;
    const handlers = map.get(event);
    const found = handlers?.find((h) => h.handler === handler);

    if (!found) {
      Logger.warn(
        `[EventService] off() called for '${event}' but this handler reference isn't registered — ` +
          `cache and reuse the same bound function for on()/off() instead of calling .bind(this) each time`
      );
      return;
    }

    map!.set(
      event,
      handlers!.filter((h) => h !== found)
    );

    for (const [handle, registered] of this.registeredHandles) {
      if (registered === found) {
        this.registeredHandles.delete(handle);
        break;
      }
    }

    Logger.debug(`[EventService] Removed handler for '${event}'`);
  }

  remove(handle: string): void {
    const found = this.registeredHandles.get(handle);
    if (!found) {
      Logger.warn(`[EventService] No handler found for handle: ${handle}`);
      return;
    }

    const map = found.event.includes('*') ? this.wildcardListeners : this.listeners;
    const handlers = map.get(found.event);
    if (!handlers) return;

    map.set(
      found.event,
      handlers.filter((h) => h !== found)
    );
    this.registeredHandles.delete(handle);

    Logger.debug(`[EventService] Removed handler (handle: ${handle})`);
  }

  private routeEvent(event: string, eventInstance: BaseEvent): void {
    const exactHandlers = this.listeners.get(event) || [];
    const wildcardHandlers = this.matchWildcardHandlers(event);
    const allHandlers = [...exactHandlers, ...wildcardHandlers];

    Logger.debug(`[EventService] Routing '${event}' to ${allHandlers.length} handler(s)`);
    Logger.debug('🔍 [DEBUG] EventService routing eventInstance:', eventInstance);
    Logger.debug('🔍 [DEBUG] EventService eventInstance keys:', Object.keys(eventInstance));

    if (!event) {
      Logger.warn(`[EventService] Event type is undefined, cannot route event`, eventInstance);
      return;
    }

    for (const { handler } of allHandlers) {
      try {
        //Logger.debug('[EventService] TBL Executing handler:', handler);
        handler(eventInstance);
      } catch (e) {
        Logger.error(`[EventService] Handler error for event '${event}'`, e);
      }
    }
  }

  private matchWildcardHandlers(event: string): RegisteredHandler[] {
    const matched: RegisteredHandler[] = [];

    for (const [pattern, handlers] of this.wildcardListeners.entries()) {
      const regex = new RegExp('^' + pattern.replace(/\./g, '\\.').replace(/\*/g, '.*') + '$');
      if (regex.test(event)) {
        matched.push(...handlers);
      }
    }

    return matched;
  }

  private flushQueue(): void {
    Logger.debug(`[EventService] Flushing ${this.eventQueue.length} queued events`);
    for (const { event_type, payload, broadcast } of this.eventQueue) {
      this.emit(event_type, payload, broadcast);
    }
    this.eventQueue = [];
  }

  // Debug methods
  getDebounceStats(): { pending: number; config: DebounceConfig } {
    return {
      pending: this.pendingEvents.size,
      config: { ...this.debounceConfig },
    };
  }
}

// Create a default GlobalEventService for /events
export const GlobalEventService = EventService.getInstance();
